import { useState } from "react";
import { useTranslation } from '../i18n';
import { COLORS } from '../lib/theme';
import type { Hunt } from '../lib/types';
import { huntDisplayName } from '../lib/huntTitle';
import { compareInvitesNewestFirst } from '../lib/invitationOrder';
import { Icon } from './Icon';

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ─── Pending invites rail ─────────────────────────────────────────────────

export interface PendingInvitesRailProps {
  hunts: Hunt[];
  teamId?: string | null;
  onOpen: (hunt: Hunt) => void;
  compact?: boolean;
}

export function PendingInvitesRail({
  hunts,
  teamId = null,
  onOpen,
  compact = false,
}: PendingInvitesRailProps) {
  const { t } = useTranslation('huntRails');
  const all = hunts.filter((h) => h.status === "upcoming" && h.myRsvp === "pending");
  const invites = (teamId ? all.filter((h) => h.teamId === teamId) : all).sort(compareInvitesNewestFirst);
  if (invites.length === 0) return null;

  return (
    <div
      className="wh-card"
      style={{
        padding: 0,
        overflow: "hidden",
        position: compact ? "static" : "sticky",
        top: compact ? "auto" : 16,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "12px 14px",
          background: COLORS.primaryOpacity8,
          borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
        }}
      >
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: 7,
            background: COLORS.primary,
            color: COLORS.onPrimary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {invites.length}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>{t('pending.title')}</div>
          <div style={{ fontSize: 11, color: COLORS.outline }}>{t('pending.subtitle')}</div>
        </div>
      </div>
      <div>
        {invites.map((h, i) => (
          <PendingInviteRow
            key={h.id}
            hunt={h}
            showTeam={!teamId}
            last={i === invites.length - 1}
            onOpen={onOpen}
          />
        ))}
      </div>
    </div>
  );
}

function PendingInviteRow({
  hunt,
  showTeam,
  last,
  onOpen,
}: {
  hunt: Hunt;
  showTeam: boolean;
  last: boolean;
  onOpen: (hunt: Hunt) => void;
}) {
  const { t } = useTranslation('huntRails');
  const [hover, setHover] = useState(false);
  const [y, m, d] = hunt.date.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const dow = dt.toLocaleDateString("en-GB", { weekday: "short" });
  // CLUB-582: the card names the hunt, not its species. When the planner left
  // the name blank the team stands in, so the subtitle drops it to avoid
  // printing the same words twice.
  const title = huntDisplayName(hunt);

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => onOpen(hunt)}
      style={{
        display: "flex",
        gap: 10,
        padding: "12px",
        borderBottom: last ? "none" : `1px solid ${COLORS.surfaceContainerHighest}`,
        background: hover ? COLORS.primaryOpacity8 : "transparent",
        cursor: "pointer",
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: 38,
          textAlign: "center",
          padding: "5px 0",
          background: COLORS.surfaceContainerLowest,
          borderRadius: 6,
          border: `1px solid ${COLORS.primary}33`,
          borderLeft: `3px solid ${COLORS.primary}`,
        }}
      >
        <div
          style={{
            fontSize: 9,
            color: COLORS.outline,
            textTransform: "uppercase",
            letterSpacing: 0.4,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {dow}
        </div>
        <div
          style={{
            fontFamily: COLORS.fontDisplay,
            fontSize: 17,
            fontWeight: 500,
            lineHeight: 1.05,
            marginTop: 2,
          }}
        >
          {d}
        </div>
        <div
          style={{
            fontSize: 9,
            color: COLORS.outline,
            textTransform: "uppercase",
            fontWeight: 600,
            lineHeight: 1,
          }}
        >
          {MONTH_ABBR[m - 1]}
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
          <span style={{ fontSize: 11, color: COLORS.outline, fontWeight: 600 }}>{hunt.start}</span>
        </div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            lineHeight: 1.25,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </div>
        {showTeam && title !== hunt.team && (
          <div
            style={{
              fontSize: 11,
              color: COLORS.outline,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              marginTop: 1,
            }}
          >
            {hunt.team}
          </div>
        )}
        <div
          style={{
            fontSize: 11,
            color: COLORS.outline,
            marginTop: 4,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <Icon name="user" size={11} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            <i>{hunt.invitedBy}</i> {t('pending.invitedYouSuffix')}
          </span>
        </div>
        <button
          className="wh-btn wh-btn-primary"
          onClick={(e) => {
            e.stopPropagation();
            onOpen(hunt);
          }}
          style={{ marginTop: 8, padding: "5px 10px", fontSize: 11, height: "auto" }}
        >
          {t('pending.readAndRsvp')}
        </button>
      </div>
    </div>
  );
}

// ─── Drafts rail ──────────────────────────────────────────────────────────

export interface DraftsRailProps {
  hunts: Hunt[];
  teamId?: string | null;
  onContinue: (hunt: Hunt) => void;
  onDiscard: (hunt: Hunt) => void;
  compact?: boolean;
}

export function DraftsRail({
  hunts,
  teamId = null,
  onContinue,
  onDiscard,
  compact = false,
}: DraftsRailProps) {
  const { t } = useTranslation('huntRails');
  const all = hunts.filter((h) => h.status === "draft");
  const drafts = teamId ? all.filter((h) => h.teamId === teamId) : all;
  if (drafts.length === 0) return null;

  return (
    <div
      className="wh-card"
      style={{
        padding: 0,
        overflow: "hidden",
        position: compact ? "static" : "sticky",
        top: compact ? "auto" : 16,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "12px 14px",
          background: "rgba(255,104,31,0.08)",
          borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
        }}
      >
        <div
          style={{
            width: 22,
            height: 22,
            borderRadius: 6,
            background: COLORS.tertiary,
            color: COLORS.onTertiary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon name="edit" size={12} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>{t('drafts.title')}</div>
          <div style={{ fontSize: 11, color: COLORS.outline }}>
            {t('drafts.unfinished', { count: drafts.length })}
          </div>
        </div>
      </div>

      <div>
        {drafts.map((h, i) => (
          <DraftRailRow
            key={h.id}
            hunt={h}
            showTeam={!teamId}
            last={i === drafts.length - 1}
            onContinue={onContinue}
            onDiscard={onDiscard}
          />
        ))}
      </div>
    </div>
  );
}

function DraftRailRow({
  hunt,
  showTeam,
  last,
  onContinue,
  onDiscard,
}: {
  hunt: Hunt;
  showTeam: boolean;
  last: boolean;
  onContinue: (h: Hunt) => void;
  onDiscard: (h: Hunt) => void;
}) {
  const { t } = useTranslation('huntRails');
  const [hover, setHover] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [y, m, d] = hunt.date.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const dow = dt.toLocaleDateString("en-GB", { weekday: "short" });

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        gap: 10,
        padding: "10px 12px",
        borderBottom: last ? "none" : `1px solid ${COLORS.surfaceContainerHighest}`,
        background: hover ? "rgba(255,104,31,0.04)" : "transparent",
        cursor: "pointer",
      }}
      onClick={() => onContinue(hunt)}
    >
      <div
        style={{
          flexShrink: 0,
          width: 36,
          textAlign: "center",
          padding: "4px 0",
          background: COLORS.surface,
          borderRadius: 6,
          border: "1px dashed #ff681f88",
        }}
      >
        <div
          style={{
            fontSize: 9,
            color: COLORS.outline,
            textTransform: "uppercase",
            letterSpacing: 0.4,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {dow}
        </div>
        <div
          style={{
            fontFamily: COLORS.fontDisplay,
            fontSize: 16,
            fontWeight: 500,
            lineHeight: 1.1,
            marginTop: 2,
          }}
        >
          {d}
        </div>
        <div
          style={{
            fontSize: 9,
            color: COLORS.outline,
            textTransform: "uppercase",
            fontWeight: 600,
            lineHeight: 1,
          }}
        >
          {MONTH_ABBR[m - 1]}
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            lineHeight: 1.25,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {huntDisplayName(hunt)}
        </div>
        {showTeam && huntDisplayName(hunt) !== hunt.team && (
          <div
            style={{
              fontSize: 11,
              color: COLORS.outline,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              marginTop: 1,
            }}
          >
            {hunt.team}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.3,
              textTransform: "uppercase",
              color: COLORS.onTertiaryFixedVariant,
              background: "rgba(255,104,31,0.15)",
              padding: "2px 6px",
              borderRadius: 3,
            }}
          >
            {hunt.draftStep || t('drafts.draftBadge')}
          </span>
          <span style={{ fontSize: 11, color: COLORS.outline }}>{hunt.start}</span>
        </div>

        {/* Actions are available on every draft row (not just the hovered one). */}
        <div style={{ display: "flex", gap: 6, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
          {confirming ? (
            <>
              <span style={{ fontSize: 11, color: COLORS.outline }}>{t('drafts.confirmDiscard')}</span>
              <button
                className="wh-btn wh-btn-outline"
                style={{ padding: "4px 8px", fontSize: 11, height: "auto" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirming(false);
                }}
              >
                {t('drafts.cancel')}
              </button>
              <button
                className="wh-btn wh-btn-danger"
                style={{ padding: "4px 8px", fontSize: 11, height: "auto" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onDiscard(hunt);
                }}
              >
                {t('drafts.discard')}
              </button>
            </>
          ) : (
            <>
              <button
                className="wh-btn wh-btn-primary"
                style={{
                  padding: "4px 8px",
                  fontSize: 11,
                  background: COLORS.tertiary,
                  borderColor: COLORS.tertiary,
                  height: "auto",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onContinue(hunt);
                }}
              >
                {t('drafts.continue')}
              </button>
              <button
                className="wh-btn wh-btn-outline"
                style={{ padding: "4px 8px", fontSize: 11, height: "auto" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirming(true);
                }}
              >
                {t('drafts.discard')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
